import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { API_URL } from './pdfConstants';
import { throttledFetch, verifyImageIntegrity } from './pdfUtils';

const BUFFER_PAGES = 12;
const PAGE_ASPECT_RATIO = 792 / 612;
const HD_DELAY_MS = 600; // Esperar 600ms quieto antes de pedir HD (suficiente para detectar que el usuario se detuvo)

const ContinuousPage = React.memo(({ 
  pageNum, documentId, scale, nightMode, rotation, 
  isVisible, isSuperAdmin, pageWidth,
  blobCache, isHdTarget, isBookmarked
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [quality, setQuality] = useState('low');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retryTrigger, setRetryTrigger] = useState(0);
  const loadingRef = useRef(false);
  const hdTimerRef = useRef(null);
  const hdAbortRef = useRef(null);
  const hdDoneRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // === CARGAR LOW (con retry y cache-first) ===
  // FIX 2026-04: No resetear blobUrl/hdDoneRef al perder visibilidad temporalmente
  // (ej: durante zoom reflow). Solo cancelar fetches en vuelo. Esto evita el flash
  // a LOW + recarga HD en cada step de zoom.
  useEffect(() => {
    if (!isVisible) {
      // Solo cancelar fetches en vuelo. NO resetear estado visible (blobUrl, hdDoneRef).
      if (hdTimerRef.current) {
        clearTimeout(hdTimerRef.current);
        hdTimerRef.current = null;
      }
      if (hdAbortRef.current) {
        try { hdAbortRef.current.abort(); } catch (_) {}
      }
      return;
    }

    // Variables compartidas con el cleanup
    const token = localStorage.getItem('token');
    const controller = new AbortController();
    let cancelled = false;
    let lowDebounceTimer = null;

    const fetchWithRetry = async (attempt = 0) => {
      const MAX_ATTEMPTS = 3;
      const DELAYS = [0, 1000, 2500]; // antes del attempt 1, 2, 3

      if (DELAYS[attempt] > 0) {
        await new Promise(r => setTimeout(r, DELAYS[attempt]));
      }
      if (cancelled || !mountedRef.current) return;

      try {
        const r = await throttledFetch(
          `${API_URL}/api/documentos/manuales/${documentId}/visor/pagina/${pageNum}?quality=low`,
          { headers: { 'Authorization': `Bearer ${token}` }, signal: controller.signal }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        // Verificar integridad: tamaño esperado + JPEG EOI
        const expectedSize = parseInt(r.headers.get('X-Image-Size') || '0');
        const integrity = await verifyImageIntegrity(blob, expectedSize);
        if (!integrity.isComplete) {
          console.warn(`[Continuous] JPEG corrupto pag ${pageNum}: ${integrity.reason || 'size_mismatch'} (${blob.size} bytes) - reintentando`);
          throw new Error('JPEG_TRUNCATED');  // Dispara el retry de fetchWithRetry
        }
        if (cancelled || controller.signal.aborted || !mountedRef.current) return;
        const url = URL.createObjectURL(blob);
        if (blobCache?.current) blobCache.current.set(`${pageNum}_low`, url);
        setBlobUrl(url);
        setQuality('low');
        setLoading(false);
        setFailed(false);
        loadingRef.current = false;
      } catch (err) {
        if (err.name === 'AbortError' || cancelled || !mountedRef.current) {
          loadingRef.current = false;
          return;
        }
        if (attempt + 1 < MAX_ATTEMPTS) {
          // Reintentar
          fetchWithRetry(attempt + 1);
        } else {
          // Último intento fallido
          if (mountedRef.current) {
            setFailed(true);
            setLoading(false);
          }
          loadingRef.current = false;
        }
      }
    };

    // Async IIFE: validar HD cacheado primero (FIX J), luego LOW cache, luego fetch
    (async () => {
      // FIX J: Validar integridad del HD cacheado ANTES de servirlo
      // (protege contra cache corrupto de sesion previa)
      const hdCached = blobCache?.current?.get(`${pageNum}_high`);
      if (hdCached) {
        try {
          const verifyResp = await fetch(hdCached);
          if (cancelled || !mountedRef.current) return;
          const verifyBlob = await verifyResp.blob();
          if (cancelled || !mountedRef.current) return;
          const integrityOk = await verifyImageIntegrity(verifyBlob, 0);
          if (integrityOk.isComplete && verifyBlob.size > 500) {
            setBlobUrl(hdCached);
            setQuality('high');
            hdDoneRef.current = true;
            setLoading(false);
            setFailed(false);
            return;
          }
          // Cache corrupto: evict y caer al flujo normal
          console.warn(`[Continuous] HD cache corrupto pag ${pageNum}, evictando`);
          try { URL.revokeObjectURL(hdCached); } catch (_) {}
          blobCache?.current?.delete(`${pageNum}_high`);
        } catch (_) {
          blobCache?.current?.delete(`${pageNum}_high`);
        }
      }

      if (cancelled || !mountedRef.current) return;

      // Cache LOW (sin validar - LOW es chico y re-fetchearlo es trivial)
      const lowCached = blobCache?.current?.get(`${pageNum}_low`);
      if (lowCached) {
        setBlobUrl(lowCached);
        setQuality('low');
        setLoading(false);
        setFailed(false);
        return;
      }

      // Si ya tenemos blobUrl renderizado, no re-fetchear
      if (blobUrl || loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      setFailed(false);

      // FIX D: Debounce 300ms para no disparar fetches durante scroll furioso
      lowDebounceTimer = setTimeout(() => {
        fetchWithRetry(0);
      }, 300);
    })();

    return () => {
      if (lowDebounceTimer) clearTimeout(lowDebounceTimer);
      cancelled = true;
      controller.abort();
      loadingRef.current = false;
    };
  }, [isVisible, documentId, pageNum, blobUrl, blobCache, retryTrigger]);

  // === UPGRADE HD (con delay para evitar fetches durante scroll rapido) ===
  useEffect(() => {
    // Limpiar timer anterior siempre
    if (hdTimerRef.current) {
      clearTimeout(hdTimerRef.current);
      hdTimerRef.current = null;
    }

    // Condiciones para intentar HD
    if (!isHdTarget || !isVisible || hdDoneRef.current || quality !== 'low') return;

    // Esperar HD_DELAY_MS para confirmar que el usuario se detuvo en esta pagina
    hdTimerRef.current = setTimeout(() => {
      if (!mountedRef.current || hdDoneRef.current) return;

      // Cache HD
      const hdCached = blobCache?.current?.get(`${pageNum}_high`);
      if (hdCached) {
        setBlobUrl(hdCached);
        setQuality('high');
        hdDoneRef.current = true;
        return;
      }

      const token = localStorage.getItem('token');
      const controller = new AbortController();
      hdAbortRef.current = controller;

      // FIX G: HD con retry + throttledFetch + integrity check (espejo de LOW)
      const fetchHdWithRetry = async (attempt = 0) => {
        const MAX = 3;
        const DELAYS = [0, 1500, 3500];
        if (DELAYS[attempt] > 0) {
          await new Promise(r => setTimeout(r, DELAYS[attempt]));
        }
        if (controller.signal.aborted || !mountedRef.current || hdDoneRef.current) return;

        try {
          const r = await throttledFetch(
            `${API_URL}/api/documentos/manuales/${documentId}/visor/pagina/${pageNum}?quality=high&cb=${Date.now()}`,
            { headers: { 'Authorization': `Bearer ${token}` }, signal: controller.signal, cache: 'no-store' }
          );
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const blob = await r.blob();
          if (controller.signal.aborted || !mountedRef.current) return;

          // Integrity check
          const expectedSize = parseInt(r.headers.get('X-Image-Size') || '0');
          const integrity = await verifyImageIntegrity(blob, expectedSize);
          if (!integrity.isComplete) throw new Error('HD_TRUNCATED');

          if (blob.size > 0) {
            const url = URL.createObjectURL(blob);
            if (blobCache?.current) blobCache.current.set(`${pageNum}_high`, url);
            setBlobUrl(url);
            setQuality('high');
            hdDoneRef.current = true;
          }
        } catch (err) {
          if (err.name === 'AbortError' || !mountedRef.current) return;
          if (attempt + 1 < MAX) {
            fetchHdWithRetry(attempt + 1);
          }
          // Si agotó retries, se queda en LOW silenciosamente
        }
      };
      fetchHdWithRetry(0);
    }, HD_DELAY_MS);

    return () => {
      if (hdTimerRef.current) clearTimeout(hdTimerRef.current);
      if (hdAbortRef.current) hdAbortRef.current.abort();
    };
  }, [isHdTarget, isVisible, quality, documentId, pageNum, blobCache]);

  const scaledWidth = pageWidth * scale;
  const scaledHeight = scaledWidth * PAGE_ASPECT_RATIO;

  return (
    <div
      data-page-num={pageNum}
      className="relative flex-shrink-0 mx-auto"
      style={{
        width: `${scaledWidth}px`,
        minHeight: `${scaledHeight}px`,
        marginBottom: '16px',
      }}
    >
      {/* Cintita ámbar para páginas marcadas (bookmark) */}
      {isBookmarked && (
        <div
          className="absolute top-0 left-0 z-10 pointer-events-none flex items-center"
          aria-label={`Página ${pageNum} marcada`}
          data-testid={`bookmark-ribbon-${pageNum}`}
        >
          <div className="w-1.5 h-16 bg-gradient-to-b from-amber-300 to-amber-500 rounded-r-sm shadow-md shadow-amber-500/30"></div>
        </div>
      )}
      {/* Numero de pagina + indicador HD */}
      <div className="absolute -left-14 top-2 text-xs font-mono w-12 text-right hidden md:flex items-center gap-1 justify-end">
        <span className="text-slate-500">{pageNum}</span>
        {quality === 'high' && (
          <span className="text-amber-400 text-[10px] font-bold">HD</span>
        )}
      </div>

      {blobUrl ? (
        <img
          src={blobUrl}
          alt={`Pagina ${pageNum}`}
          data-testid={`continuous-page-${pageNum}`}
          className={`w-full h-auto shadow-lg shadow-black/30 rounded-sm ${nightMode ? 'invert brightness-90 hue-rotate-180' : ''}`}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            pointerEvents: isSuperAdmin ? 'auto' : 'none',
            display: 'block',
            transform: rotation ? `rotate(${rotation}deg)` : undefined,
          }}
          draggable={false}
          decoding="async"
        />
      ) : loading ? (
        <div 
          className="w-full bg-slate-800 rounded-sm flex items-center justify-center shadow-lg"
          style={{ height: `${scaledHeight}px` }}
        >
          <div className="text-center">
            <Loader2 className="h-8 w-8 text-teal-400 animate-spin mx-auto mb-2" />
            <span className="text-slate-500 text-xs">Pag. {pageNum}</span>
          </div>
        </div>
      ) : failed ? (
        <div 
          className="w-full bg-slate-800/50 rounded-sm flex items-center justify-center border border-slate-700 shadow-lg"
          style={{ height: `${scaledHeight}px` }}
        >
          <div className="text-center">
            <span className="text-slate-500 text-sm block mb-3">Error en pagina {pageNum}</span>
            <button
              onClick={() => {
                setFailed(false);
                setLoading(false);
                loadingRef.current = false;
                setRetryTrigger(t => t + 1);
              }}
              className="px-4 py-2 bg-teal-500/20 hover:bg-teal-500/40 text-teal-300 hover:text-white text-sm font-medium rounded-md border border-teal-500/40 transition-colors"
              data-testid={`continuous-retry-${pageNum}`}
            >
              Reintentar carga
            </button>
          </div>
        </div>
      ) : (
        <div 
          className="w-full bg-slate-800/30 rounded-sm shadow-lg"
          style={{ height: `${scaledHeight}px` }}
        />
      )}
    </div>
  );
});

ContinuousPage.displayName = 'ContinuousPage';


const PDFContinuousView = ({
  documentId, numPages, pageNumber, goToPage,
  scale, nightMode, rotation, isSuperAdmin, containerRef,
  bookmarkedPages
}) => {
  const [visibleRange, setVisibleRange] = useState({ start: 1, end: 1 });
  const [currentVisiblePage, setCurrentVisiblePage] = useState(pageNumber || 1);
  const currentVisiblePageRef = useRef(pageNumber || 1);
  const lastGoToPageRef = useRef(null);
  const blobCache = useRef(new Map());
  // FIX H: Bloquear handleScroll temporalmente despues de un cambio de zoom
  // (el reflow dispara scroll events falsos que harian saltar de pagina).
  const ignoreScrollUntilRef = useRef(0);

  // Limpiar cache al desmontar o cambiar de documento
  useEffect(() => {
    return () => {
      blobCache.current.forEach(url => URL.revokeObjectURL(url));
      blobCache.current.clear();
    };
  }, [documentId]);

  const pageWidth = 612;

  // Paginas visibles con buffer
  const isPageVisible = useCallback((pg) => {
    const start = Math.max(1, visibleRange.start - BUFFER_PAGES);
    const end = Math.min(numPages, visibleRange.end + BUFFER_PAGES);
    return pg >= start && pg <= end;
  }, [visibleRange, numPages]);

  // HD target: pagina actual ±1
  const hdTargetSet = new Set();
  for (let i = Math.max(1, currentVisiblePage - 1); i <= Math.min(numPages, currentVisiblePage + 1); i++) {
    hdTargetSet.add(i);
  }

  // IntersectionObserver + scroll para detectar paginas visibles
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || numPages === 0) return;

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        // FIX H: Si estamos dentro del bypass post-zoom, ignorar este scroll
        if (Date.now() < ignoreScrollUntilRef.current) { ticking = false; return; }
        const containerRect = container.getBoundingClientRect();
        const centerY = containerRect.top + containerRect.height / 2;
        
        const pages = container.querySelectorAll('[data-page-num]');
        let closestPage = currentVisiblePageRef.current;
        let closestDist = Infinity;
        
        pages.forEach(el => {
          const rect = el.getBoundingClientRect();
          const pageCenterY = rect.top + rect.height / 2;
          const dist = Math.abs(pageCenterY - centerY);
          if (dist < closestDist) {
            closestDist = dist;
            closestPage = parseInt(el.dataset.pageNum) || closestPage;
          }
        });
        
        if (closestPage !== currentVisiblePageRef.current) {
          currentVisiblePageRef.current = closestPage;
          setCurrentVisiblePage(closestPage);
        }
        ticking = false;
      });
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        let minVisible = numPages + 1;
        let maxVisible = 0;

        entries.forEach(entry => {
          const pg = parseInt(entry.target.dataset.pageNum);
          if (isNaN(pg)) return;
          if (entry.isIntersecting) {
            if (pg < minVisible) minVisible = pg;
            if (pg > maxVisible) maxVisible = pg;
          }
        });

        if (minVisible <= maxVisible) {
          setVisibleRange(prev => {
            if (prev.start === minVisible && prev.end === maxVisible) return prev;
            return { start: minVisible, end: maxVisible };
          });
        }
      },
      { root: container, rootMargin: '400px 0px', threshold: [0, 0.1] }
    );

    const pageElements = container.querySelectorAll('[data-page-num]');
    pageElements.forEach(el => observer.observe(el));

    handleScroll();

    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', handleScroll);
    };
  }, [numPages, containerRef]);

  // Sincronizar pagina visible con toolbar
  useEffect(() => {
    if (currentVisiblePage !== pageNumber && !lastGoToPageRef.current) {
      goToPage(currentVisiblePage);
    }
  }, [currentVisiblePage, pageNumber, goToPage]);

  // Scroll desde toolbar
  useEffect(() => {
    if (!containerRef?.current) return;
    const container = containerRef.current;
    const targetEl = container.querySelector(`[data-page-num="${pageNumber}"]`);
    
    if (targetEl && lastGoToPageRef.current === pageNumber) {
      lastGoToPageRef.current = null;
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pageNumber, containerRef]);

  // FIX H — Al cambiar el zoom, preservar la pagina actual y bloquear
  // temporalmente el handleScroll para que el reflow no dispare goToPage falsos.
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || numPages === 0) return;
    const targetPage = currentVisiblePageRef.current;
    if (targetPage < 1) return;

    // Bloquear handleScroll por 500ms mientras ocurre el reflow
    ignoreScrollUntilRef.current = Date.now() + 500;

    // Scroll a la pagina actual despues de que el reflow termine
    const t = setTimeout(() => {
      const targetEl = container.querySelector(`[data-page-num="${targetPage}"]`);
      if (targetEl) targetEl.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 50);

    return () => clearTimeout(t);
  }, [scale, containerRef, numPages]);

  // FIX I — Al montar (ej: switch de single-page a continuo, o fullscreen toggle),
  // si pageNumber > 1 scrollear a esa pagina en vez de arrancar en 1.
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !pageNumber || pageNumber <= 1) return;
    // Esperar un tick para que los elementos [data-page-num] esten en el DOM
    const t = setTimeout(() => {
      const targetEl = container.querySelector(`[data-page-num="${pageNumber}"]`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'auto', block: 'start' });
        currentVisiblePageRef.current = pageNumber;
        setCurrentVisiblePage(pageNumber);
      }
    }, 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div 
      className="flex flex-col items-center py-4 px-2 md:px-8 w-full"
      data-testid="continuous-view-container"
    >
      <div className="sticky top-0 z-10 mb-4 bg-slate-800/80 backdrop-blur-sm rounded-full px-4 py-1.5 border border-slate-700/50 shadow-lg">
        <span className="text-xs text-slate-400">
          Pag. <span className="text-teal-400 font-medium">{currentVisiblePage}</span> de {numPages}
          <span className="text-slate-600 mx-2">|</span>
          Lectura Continua
        </span>
      </div>

      {Array.from({ length: numPages }, (_, i) => i + 1).map((pg) => (
        <ContinuousPage
          key={pg}
          pageNum={pg}
          documentId={documentId}
          scale={scale}
          nightMode={nightMode}
          rotation={rotation}
          isVisible={isPageVisible(pg)}
          isHdTarget={hdTargetSet.has(pg)}
          isSuperAdmin={isSuperAdmin}
          pageWidth={pageWidth}
          blobCache={blobCache}
          isBookmarked={!!(bookmarkedPages && bookmarkedPages.has(pg))}
        />
      ))}

      <div className="text-center py-8 text-slate-500 text-sm">
        Fin del documento ({numPages} paginas)
      </div>
    </div>
  );
};

export default PDFContinuousView;
